import { DEFAULT_TEMPLATE_FORM } from './templateConstants';
import { getRejectionReason } from './getRejectionReason';

const PROVIDER_COMPONENT_ORDER = ['header', 'body', 'footer', 'buttons'];
const PROVIDER_KEY_TO_META_TYPE = {
  header: 'HEADER',
  body: 'BODY',
  footer: 'FOOTER',
  buttons: 'BUTTONS',
};

function isProviderComponentsMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if ('type' in value) return false;
  return Object.keys(value).some((key) => PROVIDER_KEY_TO_META_TYPE[key.toLowerCase()]);
}

/** TrustSignal / provider map → Meta component array used by the template editor. */
export function convertProviderComponentsMap(componentsMap = {}) {
  const entries = Object.entries(componentsMap).filter(([, value]) => value != null);
  if (!entries.length) return null;

  const sorted = [...entries].sort(([a], [b]) => {
    const left = PROVIDER_COMPONENT_ORDER.indexOf(a.toLowerCase());
    const right = PROVIDER_COMPONENT_ORDER.indexOf(b.toLowerCase());
    return (left === -1 ? 99 : left) - (right === -1 ? 99 : right);
  });

  const result = [];
  sorted.forEach(([key, value]) => {
    const metaType = PROVIDER_KEY_TO_META_TYPE[key.toLowerCase()] || key.toUpperCase();

    if (metaType === 'BUTTONS') {
      const buttons = Array.isArray(value) ? value : value?.buttons ?? [];
      if (buttons.length) result.push({ type: 'BUTTONS', buttons });
      return;
    }

    if (typeof value !== 'object') return;

    const { type: providerType, ...rest } = value;
    const component = { ...rest, type: metaType };

    if (metaType === 'HEADER' && !component.format && providerType) {
      component.format = String(providerType).toUpperCase();
    }

    result.push(component);
  });

  return result.length ? result : null;
}

function normalizeParsedComponents(value) {
  if (Array.isArray(value)) return value;
  if (isProviderComponentsMap(value)) {
    return convertProviderComponentsMap(value) ?? DEFAULT_TEMPLATE_FORM.components;
  }
  return DEFAULT_TEMPLATE_FORM.components;
}

/** Ensure template components is always an array (handles JSON strings / provider shapes). */
export function normalizeComponents(components, fallback) {
  if (Array.isArray(components)) return components;
  if (Array.isArray(fallback)) return fallback;

  if (typeof components === 'string') {
    try {
      return normalizeParsedComponents(JSON.parse(components));
    } catch {
      // ignore invalid JSON
    }
  }

  if (isProviderComponentsMap(components)) {
    return convertProviderComponentsMap(components) ?? DEFAULT_TEMPLATE_FORM.components;
  }

  if (typeof fallback === 'string') {
    try {
      return normalizeParsedComponents(JSON.parse(fallback));
    } catch {
      // ignore invalid JSON
    }
  }

  if (isProviderComponentsMap(fallback)) {
    return convertProviderComponentsMap(fallback) ?? DEFAULT_TEMPLATE_FORM.components;
  }

  return DEFAULT_TEMPLATE_FORM.components;
}

export function normalizeTemplate(raw = {}) {
  const failedReason = getRejectionReason(raw);

  return {
    ...raw,
    name: raw.name ?? '',
    language: raw.language ?? raw.code ?? '',
    category: raw.category ?? 'UTILITY',
    status: raw.status ?? '',
    bot_number: raw.bot_number ?? raw.botNumber ?? '',
    description: raw.description ?? '',
    components: normalizeComponents(raw.components, raw.g_data),
    param: Array.isArray(raw.param) ? raw.param : [],
    media: raw.media ?? null,
    wa_Id: raw.wa_Id ?? raw.wa_id ?? raw.id ?? null,
    md_id: raw.md_id ?? raw.template_id ?? null,
    ts_id: raw.ts_id ?? null,
    updated_at: raw.updated_at ?? null,
    app_name: raw.app_name ?? '',
    failed_reason: failedReason || raw.failed_reason || '',
    is_archived: Boolean(raw.is_archived),
    archived_at: raw.archived_at ?? null,
    archived_by: raw.archived_by ?? '',
    archive_delete_wait_days: raw.archive_delete_wait_days ?? null,
    delete_eligible_at: raw.delete_eligible_at ?? null,
    tag_ids: Array.isArray(raw.tag_ids) ? raw.tag_ids : [],
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    shortlink:
      raw.shortlink !== undefined && raw.shortlink !== null && `${raw.shortlink}`.trim() !== ''
        ? `${raw.shortlink}`.trim()
        : DEFAULT_TEMPLATE_FORM.shortlink,
  };
}

export function normalizeTemplateList(rawList = []) {
  return (Array.isArray(rawList) ? rawList : []).map(normalizeTemplate);
}

export function buildCloneFormValues(template) {
  const formValues = templateToFormValues(template);
  const baseName = formValues.name || 'template';
  return {
    ...formValues,
    name: `${baseName}_copy`.slice(0, 512),
  };
}

export function templateToFormValues(template) {
  const normalized = normalizeTemplate(template);
  return {
    name: normalized.name,
    language: normalized.language,
    category: normalized.category,
    description: normalized.description,
    components: normalized.components,
    param: normalized.param,
    media: normalized.media,
    bot_number: normalized.bot_number,
    tag_ids: normalized.tag_ids,
    shortlink: normalized.shortlink,
  };
}

export function buildCreateTemplatePayload(form, buttons, keyStakeholders = []) {
  return {
    ...form,
    keyStakeholders: keyStakeholders.map((s) => s?.value ?? s),
    components: mergeButtonComponents(form.components, buttons),
  };
}

export function buildUpdateTemplatePayload(form, buttons, keyStakeholders = []) {
  return buildCreateTemplatePayload(form, buttons, keyStakeholders);
}

function mergeButtonComponents(components = [], buttons = {}) {
  const withoutButtons = components.filter((c) => c.type !== 'BUTTONS');
  const allButtons = [
    ...(buttons.quickReply ?? []),
    ...(buttons.callAction ?? []),
  ].filter(Boolean);

  if (!allButtons.length) return withoutButtons;

  return [...withoutButtons, { type: 'BUTTONS', buttons: allButtons }];
}

export function getOrganisationNameFromTemplate(template = {}) {
  return (
    template.organisation_name
    || template.company_name
    || template.organisation?.organisation_name
    || template.organisation?.name
    || ''
  ).trim();
}

export function mapOrganisationToSelectOption(org = {}) {
  if (!org?.organisation_id && !org?.id) return null;
  const id = org.organisation_id ?? org.id;
  const label = org.organisation_name ?? org.name ?? org.label ?? String(id);
  return {
    ...org,
    id,
    organisation_id: id,
    label,
    value: id,
  };
}

export function mapBotToSelectOption(bot = {}) {
  if (!bot?.wa_phone_number) return null;
  return {
    ...bot,
    id: bot.wa_phone_number,
    label: bot.app_name
      ? `${bot.app_name} (${bot.wa_phone_number})`
      : bot.wa_phone_number,
    value: bot.wa_phone_number,
  };
}

export function normalizeWabaApp(raw = {}) {
  return {
    ...raw,
    app_name: raw.app_name || raw.display_name || raw.source_name || 'WhatsApp App',
    wa_phone_number: raw.wa_phone_number || 'N/A',
    is_active:
      raw.is_active === true ||
      raw.status === 'active' ||
      raw.webhook?.registered === true,
    limit: raw.limit || 'N/A',
  };
}
